/**
 * Calculation-progress tracker rendered below the sub-agent param card
 * while a forecast calculation is in flight.
 *
 * Spec: v2-design/CALC_PROGRESS_v0_spec.md §3.3.
 *
 * Receives a calc_progress Turn (the row's ContentJson.title +
 * ContentJson.rows). Renders one row per RowIdentifier with a status
 * glyph and — when the backend provided operation counts — a counter
 * and a linear progress bar. ES rows have no operation counts and
 * render as title + status icon only.
 *
 * Visual treatment: solid info-tinted backgrounds only, no gradients —
 * a subtle info-tint wrapper (6% alpha) behind the row list, and for
 * the per-row progress bar a solid info-color foreground on a 15%-alpha
 * track. The row's status spinner is centered via alignItems="center"
 * on the ListItem. The title is rendered verbatim (no case transform);
 * the backend already sends it in sentence case.
 *
 * Backend-driven title (per spec §2.4): "Driver analysis in progress"
 * for DA, "Recalculation in progress" for ES.
 */
import React, { Fragment } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { CalcProgressRow, CalcProgressRowStatus } from './types';

interface CalcStatusProps {
  title: string;
  rows: CalcProgressRow[];
}

const STATUS_ICON_SIZE = 22;

export const CalcStatus: React.FC<CalcStatusProps> = ({ title, rows }) => {
  const theme = useTheme();

  // Indeterminate mode — backend emitted a calc_progress row with no
  // per-RowIdentifier breakdown (CV doesn't write CalculationInvocation
  // rows, so there's nothing to aggregate). Render title + spinner only.
  // Same wrapper styling as the determinate mode so the user sees a
  // consistent "something is happening" component across DA / CV / ES.
  if (rows.length === 0) {
    return (
      <Box
        sx={{
          maxWidth: 550,
          width: '100%',
          padding: 2.5,
          backgroundColor: alpha(theme.palette.info.main, 0.06),
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CircularProgress
          size={STATUS_ICON_SIZE}
          thickness={5}
          sx={{ color: 'info.main', flexShrink: 0 }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {title}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 550,
        width: '100%',
        padding: 2.5,
      }}
    >
      <Typography
        sx={{
          lineHeight: 'inherit',
          marginBottom: 2,
          fontWeight: 600,
          fontSize: '0.85rem',
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          width: '100%',
          // Subtle info-tinted wrapper. Reads as "in-flight, transient"
          // without competing with surrounding chat content. Revisit
          // in-browser if it doesn't feel right.
          backgroundColor: alpha(theme.palette.info.main, 0.06),
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <List
          sx={{
            width: '100%',
            maxHeight: '40vh',
            overflow: 'auto',
            overflowX: 'hidden',
            padding: 0,
            paddingY: 0.5,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': {
              background: alpha(theme.palette.info.main, 0.05),
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.info.main, 0.2),
              borderRadius: '3px',
              '&:hover': { background: alpha(theme.palette.info.main, 0.3) },
            },
          }}
        >
          {rows.map((row, index) => (
            <Fragment key={row.row_identifier_id}>
              <ListItem
                alignItems="center"
                sx={{
                  paddingY: 1,
                  paddingX: 1.5,
                }}
                secondaryAction={<StatusIcon status={row.status} />}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '0.85rem',
                      }}
                    >
                      {row.name}
                    </Typography>
                  }
                  secondary={<RowBody row={row} />}
                />
              </ListItem>
              {index < rows.length - 1 && (
                <Divider sx={{ margin: 0, opacity: 0.3 }} />
              )}
            </Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );
};

/**
 * Optional per-row body — operation counter + progress bar. Only renders
 * when the backend reported operation counts (DA case). ES rows pass 0/0
 * and render no body.
 */
const RowBody: React.FC<{ row: CalcProgressRow }> = ({ row }) => {
  const theme = useTheme();
  // Suppress the bar entirely until there's actual progress to show.
  // Early in a DA run, total_operations is stamped by the C# Worker
  // before completed_operations starts incrementing — rendering the
  // track at 0% leaves a wide thin strip that visually collides with
  // the spinner. The status icon alone communicates "in flight."
  if (row.total_operations <= 0 || row.completed_operations <= 0) {
    return null;
  }
  const pct = Math.round(
    (row.completed_operations / Math.max(row.total_operations, 1)) * 100
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: '12px',
        }}
      >
        {row.completed_operations} / {row.total_operations} operations
      </Typography>
      <Box
        sx={{
          width: '100%',
          height: 5,
          borderRadius: '3px',
          background: alpha(theme.palette.info.main, 0.15),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            background: theme.palette.info.main,
            borderRadius: '3px',
            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </Box>
    </Box>
  );
};

const StatusIcon: React.FC<{ status: CalcProgressRowStatus }> = ({
  status,
}) => {
  if (status === 'finished') {
    return (
      <Tooltip title="Completed" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircleIcon
            sx={{ fontSize: STATUS_ICON_SIZE, color: 'success.main' }}
          />
        </Box>
      </Tooltip>
    );
  }
  if (status === 'failed') {
    return (
      <Tooltip title="Failed" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ErrorIcon sx={{ fontSize: STATUS_ICON_SIZE, color: 'error.main' }} />
        </Box>
      </Tooltip>
    );
  }
  if (status === 'pending') {
    // FE-only status (never on the backend wire): a stage that has not started yet — the exec What-If propose
    // tracker lists its upcoming pipeline stages this way (2026-09-07).
    return (
      <Tooltip title="Not started" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <RadioButtonUncheckedIcon
            sx={{ fontSize: STATUS_ICON_SIZE, color: 'text.disabled' }}
          />
        </Box>
      </Tooltip>
    );
  }
  // started
  return (
    <Tooltip title="Processing" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress
          size={STATUS_ICON_SIZE}
          thickness={5}
          sx={{ color: 'info.main' }}
        />
      </Box>
    </Tooltip>
  );
};
